import { DateTime } from "luxon"
import React, { useEffect, useMemo, useRef, useState } from "react"

import i18n from "@/i18n"

import { RESERVATION_DURATION_STEP_SECONDS } from "../../../reservation_duration_options"
import {
  type AvailabilityInterval,
  isReservationEndEnabled,
  isReservationStartEnabled,
} from "../../../reservation_table_availability"

import { TS_STYLES } from "./styles"

export type TimeDropdownPanelProps = {
  /** The active reservation start ISO timestamp, if any (seeds the highlighted start). */
  startTime: string
  /** The active reservation end ISO timestamp, if any (seeds the highlighted end). */
  endTime: string
  /** Every opening-hours 15-minute slot for the date (the shared engine's `scheduleTimeOptions`). */
  timeSlots: [string, DateTime][]
  /** The selected table's busy blocks; empty when no table is selected (bounds by opening hours only). */
  busyBlocks: { startTime: string; endTime: string }[]
  /** Min-advance booking threshold for today (null on future dates → no lower bound). */
  closedUntil: DateTime | null
  /** Exact-minute opening time; disables pre-open start rows for non-:00 venues (e.g. 17:30). */
  openingOpensAt?: DateTime | null
  /** Exact-minute closing time; bounds the reservation end instead of the rounded last slot. */
  openingClosesAt?: DateTime | null
  minReservationTime?: number | null
  maxReservationTime?: number | null
  /** Commit an edited [start, end] window (drives the state machine's time-edit action). */
  onEditTime: (startTime: string, endTime: string) => void
  onClose: () => void
}

type TimeRow = {
  label: string
  dt: DateTime
  iso: string
  onTheHour: boolean
  enabled: boolean
  selected: boolean
}

/**
 * Two-column start/end datetime picker (Figma 1283-13709). Each column is independently scrollable
 * and lists every opening-hours 15-minute slot, enabling only those that keep the reservation legal:
 * a start needs `minReservationTime` of free room before closing / the table's next busy block; an
 * end must land in `[start + min, min(start + max, closing, next busy block)]`. Tapping a start
 * re-anchors the window (keeping the end when it stays valid, committing immediately); tapping an end
 * commits the [start, end] window through `onEditTime` and closes the panel.
 */
const TimeDropdownPanel: React.FC<TimeDropdownPanelProps> = ({
  startTime,
  endTime,
  timeSlots,
  busyBlocks,
  closedUntil,
  openingOpensAt,
  openingClosesAt,
  minReservationTime,
  maxReservationTime,
  onEditTime,
  onClose,
}) => {
  const [draftStart, setDraftStart] = useState<string | null>(startTime || null)
  const [draftEnd, setDraftEnd] = useState<string | null>(endTime || null)

  const startColRef = useRef<HTMLDivElement>(null)
  const endColRef = useRef<HTMLDivElement>(null)
  const startAnchorRef = useRef<HTMLButtonElement>(null)
  const endAnchorRef = useRef<HTMLButtonElement>(null)

  const busyIntervals = useMemo<AvailabilityInterval[]>(
    () =>
      busyBlocks.map((block) => ({ start: DateTime.fromISO(block.startTime), end: DateTime.fromISO(block.endTime) })),
    [busyBlocks],
  )

  // Exact closing bound if known (e.g. 23:30), else the rounded last slot. The lower start bound is
  // the later of the min-advance threshold and the exact open time (so pre-open rows disable).
  const closesAt = openingClosesAt ?? (timeSlots.length ? timeSlots[timeSlots.length - 1][1] : null)
  const earliestStart = [closedUntil, openingOpensAt]
    .filter((dt): dt is DateTime => Boolean(dt))
    .reduce<DateTime | null>((latest, dt) => (!latest || dt > latest ? dt : latest), null)

  const minDurationSeconds =
    minReservationTime && minReservationTime > 0 ? minReservationTime : RESERVATION_DURATION_STEP_SECONDS
  const maxDurationSeconds = maxReservationTime && maxReservationTime > 0 ? maxReservationTime : null

  const draftStartMs = draftStart ? DateTime.fromISO(draftStart).toMillis() : null
  const draftEndMs = draftEnd ? DateTime.fromISO(draftEnd).toMillis() : null

  const startRows = useMemo<TimeRow[]>(() => {
    if (!closesAt) return []
    return timeSlots.map(([label, dt]) => ({
      label,
      dt,
      iso: dt.toISO() ?? "",
      onTheHour: dt.minute === 0,
      enabled: isReservationStartEnabled({
        start: dt,
        busyBlocks: busyIntervals,
        closesAt,
        earliestStart,
        minDurationSeconds,
      }),
      selected: dt.toMillis() === draftStartMs,
    }))
  }, [timeSlots, busyIntervals, closesAt, earliestStart, minDurationSeconds, draftStartMs])

  const earliestValidStart = useMemo(() => startRows.find((row) => row.enabled)?.dt ?? null, [startRows])
  const referenceStart = useMemo(
    () => (draftStart ? DateTime.fromISO(draftStart) : earliestValidStart),
    [draftStart, earliestValidStart],
  )

  const endRows = useMemo<TimeRow[]>(() => {
    if (!closesAt) return []
    return timeSlots.map(([label, dt]) => ({
      label,
      dt,
      iso: dt.toISO() ?? "",
      onTheHour: dt.minute === 0,
      enabled: isReservationEndEnabled({
        end: dt,
        referenceStart,
        busyBlocks: busyIntervals,
        closesAt,
        minDurationSeconds,
        maxDurationSeconds,
      }),
      selected: dt.toMillis() === draftEndMs,
    }))
  }, [timeSlots, busyIntervals, closesAt, referenceStart, minDurationSeconds, maxDurationSeconds, draftEndMs])

  const startAnchorIndex = useMemo(() => {
    const selectedIndex = startRows.findIndex((row) => row.selected)
    return selectedIndex >= 0 ? selectedIndex : startRows.findIndex((row) => row.enabled)
  }, [startRows])

  const endAnchorIndex = useMemo(() => {
    const selectedIndex = endRows.findIndex((row) => row.selected)
    return selectedIndex >= 0 ? selectedIndex : endRows.findIndex((row) => row.enabled)
  }, [endRows])

  useEffect(() => {
    const center = (container: HTMLDivElement | null, anchor: HTMLButtonElement | null) => {
      if (!container || !anchor) return
      container.scrollTop = anchor.offsetTop - container.clientHeight / 2 + anchor.offsetHeight / 2
    }
    // The panel remounts each time it opens, so centering once on mount is enough.
    center(startColRef.current, startAnchorRef.current)
    center(endColRef.current, endAnchorRef.current)
  }, [])

  const commit = (start: string, end: string) => {
    onEditTime(start, end)
    onClose()
  }

  const handleStartTap = (row: TimeRow) => {
    if (!row.enabled || !closesAt) return

    const keepsEnd =
      draftEnd &&
      isReservationEndEnabled({
        end: DateTime.fromISO(draftEnd),
        referenceStart: row.dt,
        busyBlocks: busyIntervals,
        closesAt,
        minDurationSeconds,
        maxDurationSeconds,
      })

    setDraftStart(row.iso)

    if (keepsEnd && draftEnd) {
      commit(row.iso, draftEnd)
      return
    }

    setDraftEnd(null)
  }

  const handleEndTap = (row: TimeRow) => {
    if (!row.enabled || !referenceStart) return
    setDraftEnd(row.iso)
    commit(referenceStart.toISO() ?? "", row.iso)
  }

  const rowClassName = (row: TimeRow) => {
    if (row.selected) return `${TS_STYLES.timeRow} ${TS_STYLES.timeRowSelected}`
    const base = row.onTheHour ? TS_STYLES.timeRowHour : TS_STYLES.timeRowNormal
    return `${TS_STYLES.timeRow} ${base}${row.enabled ? "" : ` ${TS_STYLES.timeRowDisabled}`}`
  }

  return (
    <div className={TS_STYLES.timePanel} role="dialog" data-testid="public-reservation-modal-mobile-time-panel">
      <div className={TS_STYLES.timePanelHeader}>
        <div className={TS_STYLES.timePanelHeaderCell}>{i18n.t("app.public.venue.table_selection.start_time")}</div>
        <div className={`${TS_STYLES.timePanelHeaderCell} ${TS_STYLES.timePanelColumnDivider}`}>
          {i18n.t("app.public.venue.table_selection.end_time")}
        </div>
      </div>

      <div className={TS_STYLES.timePanelColumns}>
        <div
          ref={startColRef}
          className={TS_STYLES.timePanelColumn}
          role="listbox"
          data-testid="public-reservation-modal-mobile-time-dropdown-start-column"
        >
          {startRows.map((row, index) => (
            <button
              key={row.iso || row.label}
              ref={index === startAnchorIndex ? startAnchorRef : undefined}
              type="button"
              role="option"
              disabled={!row.enabled}
              aria-disabled={!row.enabled}
              aria-selected={row.selected}
              className={rowClassName(row)}
              onClick={() => handleStartTap(row)}
              data-testid={`public-reservation-modal-mobile-time-dropdown-start-${row.label}`}
            >
              {row.label}
            </button>
          ))}
        </div>

        <div
          ref={endColRef}
          className={`${TS_STYLES.timePanelColumn} ${TS_STYLES.timePanelColumnDivider}`}
          role="listbox"
          data-testid="public-reservation-modal-mobile-time-dropdown-end-column"
        >
          {endRows.map((row, index) => (
            <button
              key={row.iso || row.label}
              ref={index === endAnchorIndex ? endAnchorRef : undefined}
              type="button"
              role="option"
              disabled={!row.enabled}
              aria-disabled={!row.enabled}
              aria-selected={row.selected}
              className={rowClassName(row)}
              onClick={() => handleEndTap(row)}
              data-testid={`public-reservation-modal-mobile-time-dropdown-end-${row.label}`}
            >
              {row.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TimeDropdownPanel
